import React, { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import { useAuth } from "../hooks/useAuth";
import Layout from "./Layout/Layout";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: "admin" | "proponente";
  // Modulo administrativo exigido pela rota. Casa com o modulo verificado no
  // backend (exige_modulo_administrativo) e com o card em ActionButtons.
  // Aceita lista: o relatorio de resultado pertence tanto a gestao de
  // editais quanto ao modulo de avaliacao (issue #238), e quem tem
  // apenas um dos dois precisa abrir a tela.
  requiredModulo?: string | string[];
}

const MENSAGEM_SEM_ACESSO =
  "Você não tem acesso a esta área. Fale com o administrador.";

// Avisa e devolve o usuario ao painel. Componente proprio para o aviso sair
// em efeito (fora da renderizacao) sem hook condicional no ProtectedRoute.
function SemAcessoAoModulo() {
  useEffect(() => {
    toast.error(MENSAGEM_SEM_ACESSO, { id: "sem-acesso-modulo" });
  }, []);

  return <Navigate to="/admin/home" replace />;
}

export function ProtectedRoute({
  children,
  requiredRole,
  requiredModulo,
}: ProtectedRouteProps) {
  const { isAuthenticated, user, loading } = useAuth();
  const location = useLocation();

  // Enquanto estiver carregando, mostra tela em branco ou loading
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 text-lg">
            Verificando autenticação...
          </p>
        </div>
      </div>
    );
  }

  // Se não está autenticado, redireciona para login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Se tem role obrigatório e o usuário não tem o role correto
  if (requiredRole && user?.tipo_usuario !== requiredRole) {
    // Redireciona cada tipo de usuário para sua página inicial correta
    const redirectPath =
      user?.tipo_usuario === "admin" ? "/admin/home" : "/home";
    return <Navigate to={redirectPath} replace />;
  }

  // Modulo administrativo: sem a lista de modulos (admin legado ainda sem
  // perfil) nao ha restricao conhecida e o acesso segue, como no painel.
  // A negativa real continua sendo o 403 da API.
  const modulos = user?.modulos_administrativos;
  const modulosExigidos = Array.isArray(requiredModulo)
    ? requiredModulo
    : requiredModulo
      ? [requiredModulo]
      : [];

  if (modulosExigidos.length > 0 && modulos && !modulosExigidos.some((m) => modulos.includes(m))) {
    return <SemAcessoAoModulo />;
  }

  if (
    user?.tipo_usuario === "proponente" &&
    user.has_proponente === false &&
    location.pathname !== "/completar-cadastro"
  ) {
    return <Navigate to="/completar-cadastro" replace />;
  }

  if (
    location.pathname === "/completar-cadastro" &&
    user?.tipo_usuario === "proponente" &&
    user.has_proponente === true
  ) {
    return <Navigate to="/home" replace />;
  }

  // Se está tudo certo, renderiza com Layout
  return <Layout>{children}</Layout>;
}
